/**
 * Tiện ích bảo mật cho ứng dụng
 */

/**
 * Tạo CSRF token 
 * @returns {string} Token CSRF ngẫu nhiên
 */
export const generateCSRFToken = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Lưu CSRF token vào localStorage
 */
export const storeCSRFToken = () => {
  const token = generateCSRFToken();
  localStorage.setItem('csrf_token', token);
  return token;
};

/**
 * Lấy CSRF token đã lưu
 * @returns {string|null} CSRF token hoặc null nếu không có
 */
export const getCSRFToken = () => {
  return localStorage.getItem('csrf_token');
};

/**
 * Xác thực CSRF token
 * @param {string} token - Token cần kiểm tra
 * @returns {boolean} - Kết quả xác thực
 */
export const validateCSRFToken = (token) => {
  const storedToken = getCSRFToken();
  return storedToken === token;
};

/**
 * Trình bảo vệ XSS - Lọc đầu vào để tránh tấn công XSS
 * @param {string} input - Chuỗi đầu vào cần được làm sạch
 * @returns {string} - Chuỗi đã được làm sạch
 */
export const sanitizeInput = (input) => {
  if (!input) return '';
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Bảo vệ khỏi SQL injection (cho các API calls)
 * @param {string} input - Chuỗi đầu vào cần được làm sạch
 * @returns {string} - Chuỗi đã được làm sạch
 */
export const preventSQLInjection = (input) => {
  if (!input) return '';
  
  return input
    .replace(/'/g, "''")
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '');
};

/**
 * Mã hóa dữ liệu nhạy cảm trước khi lưu trữ
 * @param {string} data - Dữ liệu cần mã hóa
 * @returns {string} - Dữ liệu đã được mã hóa
 */
export const encryptSensitiveData = (data) => {
  if (!data) return '';
  
  // Simple base64 encoding (thực tế cần dùng thuật toán mã hóa mạnh hơn)
  return btoa(data);
};

/**
 * Giải mã dữ liệu đã mã hóa
 * @param {string} encryptedData - Dữ liệu đã mã hóa
 * @returns {string} - Dữ liệu gốc
 */
export const decryptSensitiveData = (encryptedData) => {
  if (!encryptedData) return '';
  
  // Simple base64 decoding
  try {
    return atob(encryptedData);
  } catch (error) {
    console.error('Failed to decrypt data:', error);
    return '';
  }
};

/**
 * Xóa tất cả dữ liệu nhạy cảm khi đăng xuất
 */
export const clearSensitiveData = () => {
  // Xóa dữ liệu nhạy cảm khỏi localStorage
  const keysToKeep = ['language', 'theme', 'cookieConsent'];
  
  Object.keys(localStorage).forEach(key => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });
  
  // Xóa tất cả cookies ngoại trừ cookie cần thiết
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.trim().split('=');
    if (!['necessary_cookies'].includes(name)) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
};

/**
 * Nắm bắt và ghi lại các lỗi bảo mật
 * @param {Error} error - Lỗi cần ghi lại
 * @param {string} context - Ngữ cảnh xảy ra lỗi
 */
export const logSecurityError = (error, context = 'general') => {
  console.error(`Security Error [${context}]:`, error);
  
  // Trong ứng dụng thực, gửi lỗi này đến hệ thống giám sát
  if (process.env.NODE_ENV === 'production') {
    // Gửi lỗi đến hệ thống giám sát (Firebase Analytics, Sentry, v.v.)
    // analytics.logEvent('security_error', { error_message: error.message, context });
  }
};

// Tạo đối tượng trước khi export
const securityUtils = {
  generateCSRFToken,
  storeCSRFToken,
  getCSRFToken,
  validateCSRFToken,
  sanitizeInput,
  preventSQLInjection,
  encryptSensitiveData,
  decryptSensitiveData,
  clearSensitiveData,
  logSecurityError
};

export default securityUtils; 